/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useGridColumnPersistence} from '@ion/applications/src/hooks/useGridColumnPersistence';
import {useGridRowsPerPage} from '@ion/applications/src/hooks/useGridRowsPerPage';
import {useViewSubGroups} from '@ion/applications/src/hooks/useViewSubGroups';
import {
  getBulkEntityDeleteMessage,
  getEntityDeleteMessage
} from '@ion/applications/src/utils/translation';
import useHasMounted from '@ion/core/src/hooks/useHasMounted';
import {translate} from '@ion/core/src/locale/locale';
import {UserContext} from '@ion/core/src/services/MicroServices';
import {
  AccountLink,
  Asset,
  Assets,
  BulkActionEntity,
  CRUDPermissions,
  Entity,
  GenericObject,
  Link
} from '@ion/core/src/types/entities';
import {
  DownloadTemplate
} from '@ion/core/src/types/templates';
import {DropDownInfiniteScrollProps} from '@ion/dls/src/components/menus/Dropdown';
import {SnackbarMessage} from '@ion/dls/src/components/snackbars/SnackbarMessage';
import useSnackbar from '@ion/dls/src/components/snackbars/useSnackbar';
import {
  AssetDeletePrompt
} from '@ion/dls/src/editors/AssetDeletePrompt';
import {
  AssetDeleteRestrictPrompt
} from '@ion/dls/src/editors/AssetDeleteRestrictPrompt';
import {
  AssetAssociation,
  AssetEditorShowDefaultProps,
  AssetTreeRefresh
} from '@ion/dls/src/editors/AssetEditor';
import {
  AuditDateFilterProps,
  AuditDialog
} from '@ion/dls/src/editors/audit/AuditDialog';
import {
  ViewAuditChangesDialog
} from '@ion/dls/src/editors/audit/ViewAuditChangesDialog';
import {
  BulkDeletePrompt
} from '@ion/dls/src/editors/BulkDeletePrompt';
import {
  MovedEntitiesEditor
} from '@ion/dls/src/editors/MovedEntitiesEditor';
import {BulkHeader, GridOperation} from '@ion/dls/src/grid/IonGrid';
import {IonSnackbar} from '@ion/dls/src/notifications/IonSnackbar';
import Render from '@ion/dls/src/render/Render';
import {FilterChips} from '@ion/dls/src/types/editors';
import {
  ModularSearchProps,
  PageViewSubGroupsProps,
  ViewSubGroupsPosition
} from '@ion/dls/src/types/types';
import {getViewSubGroupsProps, partialStatusMessageFormatter} from '@ion/dls/src/utils/ComponentUtils';
import {exportMovedAssets} from '../../../store/actions/exporter';
import {ExportContext} from '../../../types/redux';
import {
  viewShowDialogSelector as devicesViewShowDialogSelector
} from '../../devices/selector';
import ViewDeviceEditor from '../../devices/ViewDeviceEditor';
import {ReportOperations} from '../../reports/reportsUtils';
import AdminContainer, {
  CapExportOperations,
  GridOperations,
  PageTitle
} from '../AdminContainer';
import BulkEditGroupEditor from '../groups/BulkEditGroupEditor';
import EditGroupEditor from '../groups/EditGroupEditor';
import {groupAudit} from '../groups/state';
import {
  groupAuditRecordSelector,
  groupAuditSelectedSelector,
  groupAuditShowChangesDialogSelector,
  groupAuditShowDialogSelector,
  groupEditShowDialogSelector
} from '../groups/state-selectors';
import ViewGroupEditor from '../groups/ViewGroupEditor';
import AddAssetEditor from './AddAssetEditor';
import AdminAssetsGrid, {AssetsGridColumns} from './AdminAssetsGrid';
import BulkEditAssetEditor from './BulkEditAssetEditor';
import BulkMoveAssetEditor from './BulkMoveAssetEditor';
import EditAssetEditor from './EditAssetEditor';
import ImportAssetsContainer from './ImportAssetsContainer';
import ImportCarbAssetsContainer from './ImportCarbAssetsContainer';
import {
  addAdminAsset,
  auditAdminAsset,
  deleteAdminAsset,
  deleteAdminAssets,
  deleteAsset,
  deleteAssets,
  editAdminAsset,
  importAdminAssets,
  initializeAdminAssets,
  moveAdminAssets,
  moveAssets,
  NAME,
  PREFERENCE,
  setAssetsGridPageSize,
  setAssetViewSubGroups,
  showAuditChangesDialog,
  showBulkEditGroupDialog,
  UPDATE_GRID_COLUMNS
} from './state';
import useAssetModularSearch from './useAssetModularSearch';
import useAssetMove from './useAssetMove';

export interface AdminAssetProps extends
  AssetAssociation,
  ReportOperations,
  AuditDateFilterProps,
  PageViewSubGroupsProps,
  AssetEditorShowDefaultProps,
  GridOperations,
  DropDownInfiniteScrollProps,
  BulkHeader,
  CapExportOperations,
  AssetTreeRefresh,
  ModularSearchProps,
  GridOperation {
  getRouteLabel?: (route: string, useTitleCase: boolean) => string;
  onSelectAccount?: (value: AccountLink) => void;
  refreshInitialization?: Function;
  accountHierarchy?: AccountLink[];
  defaultPageId?: string;
  context?: UserContext;
  isCtcAdmin?: boolean;
  usePreferedPageSize?: boolean;
  enableSelectionsExport?: boolean;
  globalGroup: AccountLink;
  includeGlobalSearch?: boolean;
  searchKeyword?: string;
  searchModule?: string;
  account?: Link;
  openBulkActionDialog?: Function;
  showGroup?: boolean;
  showTimeoutError?: boolean;
  showBulkEditGroup?: boolean;
  bulkEditGroupSelectedAssets?: Assets;
  filteredColumns?: AssetsGridColumns[];
  enableSelectAll?: boolean;
  includeGroupProperties?: boolean;
  includeDeviceGridCheckboxes?: boolean;
  includeAssetGridCheckboxes?: boolean;
  assetsPermissions?: CRUDPermissions;
  accountsPermissions?: CRUDPermissions;
  hasExportLimit?: boolean;
  hasCarbAssetImport?: boolean;
}

/**
 * AdminAssets
 *
 * Defines the AdminAssets container component. This component is only to render
 * child components and provide top level subscriptions and dispatches to the
 * redux store.
 */
export const AdminAssets = ({
  globalGroup,
  Features,
  onSelectAccount,
  accountHierarchy,
  enableSelectionsExport,
  bulkEditGroupSelectedAssets,
  filteredColumns,
  showAddTypeDefault,
  showAddInstallDateDefault,
  assetsPermissions,
  openBulkActionDialog,
  accountsPermissions,
  refreshInitialization,
  hasCarbAssetImport = false,
  viewSubGroupsPosition = ViewSubGroupsPosition.GRID_PAGINATION_CONTROLS,
  searchKeyword = '',
  searchModule = '',
  isCtcAdmin = false,
  showBulkHeader = false,
  includeGlobalSearch = false,
  showBulkEditGroup = false,
  includeAssetAssociation = false,
  includeFilteredAssetAssociation = false,
  showGroup = false,
  enableSubgroupToggle = false,
  enableSelectAll = false,
  isSortByAlphaNumeric = false,
  isReadOnly = false,
  includeGroupProperties = true,
  includeDeviceGridCheckboxes = false,
  includeAssetGridCheckboxes = false,
  enableDateFilter = false,
  showTimeoutError = false,
  isInfiniteScroll = false,
  hasExportLimit = false,
  isPersist = false,
  usePreferedPageSize = false,
  showNotificationDialog = false,
  showTreeRefresh = false,
  autoSelectNext = false,
  showBulkDialog = false,
  disableExport = false
}: AdminAssetProps) => {
  const dispatch  = useDispatch();
  const isMounted = useHasMounted();
  const {
    result,
    selected,
    isEditing,
    loading,
    error
  } = useSelector(({adminAssets: {destroy}}) => destroy);
  const {
    result: bulkDeleteResult,
    selected: bulkDeleteSelected,
    isEditing: bulkDeleteIsEditing,
    loading: bulkDeleteLoading,
    error: bulkDeleteError,
    record: bulkDeleteRecord
  } = useSelector(({adminAssets: {bulkDelete}}) => bulkDelete);
  const {isEditing: showAddAsset}                          = useSelector(({adminAssets: {add}}) => add);
  const {
    isEditing: showEditAsset,
    isView: showViewAsset,
    selected: selectedAsset
  }                                                        = useSelector(({adminAssets: {edit}}) => edit);
  const {isEditing: showBulkEditAsset}                     = useSelector(({adminAssets: {bulkEdit}}) => bulkEdit);
  const {isImporting: showImportAsset}                     = useSelector(({adminAssets: {bulkImport}}) => bulkImport);
  const {columnState}                                      = useSelector(({adminAssets: {grid}}) => grid);
  const showEditGroup                                      = useSelector(groupEditShowDialogSelector);
  const showAuditGroup                                     = useSelector(groupAuditShowDialogSelector);
  const auditGroup                                         = useSelector(groupAuditSelectedSelector);
  const showAuditGroupChanges                              = useSelector(groupAuditShowChangesDialogSelector);
  const {
    isEditing: showAuditAsset,
    selected: auditAsset
  } = useSelector(({adminAssets: {audit}}) => audit);
  const {
    auditRecord: selectedAuditRecord,
    showChangesDialog: shouldShowAuditChangesDialog
  } = useSelector(({adminAssets: {audit}}) => audit);
  const [isAssetSelected, setIsAssetSelected]                   = useState<boolean>(false);
  const gridRef                                                 = useRef<GenericObject>();
  const [restrictDeletePrompt, setRestrictDeletePrompt]         = useState<boolean>(false);
  const [restrictBulkDeletePrompt, setRestrictBulkDeletePrompt] = useState<boolean>(false);
  const [deleteDeviceIds, setDeleteDeviceIds]                   = useState([]);
  const [deleteAssetName, setDeleteAssetName]                   = useState<string>('');
  const [areTotalRowsSelected, setAreTotalRowsSelected]         = useState<boolean>(false);
  const [totalRowsCount, setTotalRowsCount]                     = useState(0);
  const [filterChips, setFilterChips]                           = useState<FilterChips>([]);
  const [selectedAssets, setSelectedAssets]                     = useState([]);
  const showViewDevice                                          = useSelector(devicesViewShowDialogSelector);
  const {exportLimit}                                           = useSelector(({exporter}) => exporter.current);
  const [sortOn, setSortOn]                                     = useState(['name']);
  const [showCarbImport, setShowCarbImport]                     = useState(false);

  const [viewSubGroups, toggleViewSubGroups] = useViewSubGroups({
    name       : NAME,
    action     : setAssetViewSubGroups,
    view       : !Features || enableSubgroupToggle,
    preference : PREFERENCE
  });

  const [gridPageSize, setGridPageSize] = useGridRowsPerPage({
    name       : NAME,
    action     : setAssetsGridPageSize,
    preference : PREFERENCE
  });
  
  const {
    show: showBulkSnackbar,
    message: bulkSnackbarMessage,
    openSnackbar: onComplete,
    dismissSnackbar: dismissBulkSnackbar
  } = useSnackbar();

  const auditEntity         = showAuditGroup ? Entity.group : Entity.asset;
  const auditItem           = showAuditGroup ? auditGroup : auditAsset;
  const auditSelectedRecord = showAuditGroup ? groupAuditRecordSelector : selectedAuditRecord;

  const {
    isExporting,
    movedAssets,
    showSuccess,
    onCloseSnack,
    onViewRecords,
    showMoveAsset,
    viewMovedAssets,
    movedAssetsErrors,
    selectedAssetsToMove,
    onCancelViewMovedAssetsDialog
  } = useAssetMove();

  useEffect(() => {
    if (isCtcAdmin) {
      return function() {
        dispatch(moveAssets(null));
        dispatch(moveAdminAssets(null, false));
      };
    }
  }, [isCtcAdmin]);

  const onRefresh = useCallback(function() {
    gridRef.current?.onRefresh(true);
  }, []);
  const {getSearchParams, mappings, filtersSearchCriteria} = useAssetModularSearch(onRefresh, viewSubGroups);

  useEffect(() => {
    if (!isMounted) {
      dispatch(initializeAdminAssets());
    }
  }, [isMounted]);

  const onImport = function() {
    dispatch(importAdminAssets());
  };

  const onCarbImport = function() {
    setShowCarbImport(true);
  };

  const onExport = function(downloadParams?: DownloadTemplate) {
    gridRef.current?.onExport(downloadParams);
  };

  function onToggleAddForm() {
    dispatch(addAdminAsset());
  };

  const onDeleteConfirm = useCallback((asset: Asset) => {
    if (asset?.devices?.length) {
      dispatch(deleteAdminAsset(selected, false));
      setRestrictDeletePrompt(true);
      setDeleteAssetName(asset.name);
    } else {
      dispatch(deleteAsset(selected, showTreeRefresh));
    }
  }, [selected]);

  const onDeleteRestrictCancel = useCallback(() => {
    setDeleteAssetName('');
    onBulkDeleteCancel();
    setRestrictDeletePrompt(false);
    setRestrictBulkDeletePrompt(false);
  }, []);

  function onDeleteCancel() {
    dispatch(deleteAdminAsset(selected, false));
  }

  const onAuditClose = useCallback(() => {
    if (showAuditAsset) {
      dispatch(auditAdminAsset(null, false));
    } else {
      dispatch(groupAudit(null, false));
    }
  }, [showAuditAsset, showAuditGroup]);

  const onBulkDeleteConfirm = useCallback((assets: Asset[]) => {
    const assetName = [];
    const assetIds = [];
    let confirmDelete = true;
    assets?.forEach(({id, name, devices}) => {
      if (devices?.length) {
        confirmDelete = false;
        assetName.push(name);
        assetIds.push(id);
        setDeleteAssetName(assetName);
      }
    });

    if (confirmDelete) {
      dispatch(deleteAssets(bulkDeleteSelected));
    } else {
      setDeleteDeviceIds(assetIds);
      setRestrictBulkDeletePrompt(true);
    }
  }, [bulkDeleteSelected]);

  const onBulkDeletePromptAction = useCallback(() => {
    const deleteAsseList = bulkDeleteSelected.filter(asset => deleteDeviceIds.indexOf(asset.id) < 0);
    dispatch(deleteAssets(deleteAsseList));
    setRestrictBulkDeletePrompt(false);
  }, [bulkDeleteSelected, deleteDeviceIds]);

  function onBulkDeleteCancel() {
    dispatch(deleteAdminAssets(bulkDeleteSelected, false));
  }

  const onUpdateGridColumns = useGridColumnPersistence({
    action : UPDATE_GRID_COLUMNS,
    name   : NAME,
    columnState
  });

  function onViewAuditChanges(auditRecord, show = false) {
    dispatch(showAuditChangesDialog(auditRecord, show));
  };

  function onViewAuditChangesClose() {
    dispatch(showAuditChangesDialog(null, false));
  };

  function onEdit() {
    dispatch(editAdminAsset(selectedAsset, true, false));
  }

  function onAudit() {
    dispatch(editAdminAsset(selected, false, false));
  }

  const getDeleteMessage = function() {
    if (isCtcAdmin) {
      if (selected && !bulkDeleteSelected) {
        return getEntityDeleteMessage(selected);
      }
      if (!selected && bulkDeleteSelected) {
        return getBulkEntityDeleteMessage(Entity.asset, bulkDeleteSelected);
      }
    }
  };

  const onBulkEditGroupClose = useCallback(() => {
    dispatch(showBulkEditGroupDialog(null, false));
  }, []);

  const onMovedAssetsExportClick = useCallback((items) => {
    dispatch(exportMovedAssets(items));
  }, []);

  const onBulkActionsClick = useCallback(() => {
    const title = translate(`common:editors.bulkActions.gridSelection.${areTotalRowsSelected
      ? 'selectedAll' : 'selectedEntities'}`,
    {account: globalGroup?.title, entity: NAME});
    openBulkActionDialog({
      selectedChips      : selectedAssets,
      filters            : filterChips,
      search             : filtersSearchCriteria,
      includeSubAccounts : viewSubGroups,
      entity             : BulkActionEntity.ASSETS,
      areTotalRowsSelected,
      totalRowsCount,
      onRefresh,
      title
    });
  }, [viewSubGroups, selectedAssets, areTotalRowsSelected, filterChips, filtersSearchCriteria]);

  const viewSubGroupsProps = useMemo(() => getViewSubGroupsProps(
    viewSubGroupsPosition,
    viewSubGroups as boolean,
    toggleViewSubGroups as Function
  ), [viewSubGroupsPosition, viewSubGroups, toggleViewSubGroups]);

  return (
    <>
      <AdminContainer grid={
        <AdminAssetsGrid ref={gridRef}
          selectedAssets={selectedAssets}
          hasExportLimit={hasExportLimit}
          totalRowsCount={totalRowsCount}
          isPersist={isPersist}
          exportLimit={exportLimit}
          selectedItemsCount={selectedAssets?.length}
          setSelectedAssets={setSelectedAssets}
          onBulkActionsClick={onBulkActionsClick}
          showBulkHeader={showBulkHeader}
          isSortByAlphaNumeric={isSortByAlphaNumeric}
          viewSubGroups={viewSubGroups}
          toggleViewSubGroups={toggleViewSubGroups}
          getAreTotalRowsSelected={setAreTotalRowsSelected}
          setTotalRowsCount={setTotalRowsCount}
          areTotalRowsSelected={areTotalRowsSelected}
          isCtcAdmin={isCtcAdmin}
          usePreferedPageSize={usePreferedPageSize}
          showTimeoutError={showTimeoutError}
          enableSelectAllCheckbox={enableSelectAll}
          filteredColumns={filteredColumns}
          setItemSelected={setIsAssetSelected}
          includeGlobalSearch={includeGlobalSearch}
          searchKeyword={searchKeyword}
          searchModule={searchModule}
          enableSelectionsExport={enableSelectionsExport}
          onSelectAccount={onSelectAccount}
          accountHierarchy={accountHierarchy}
          gridPageSize={gridPageSize}
          setSortOn={setSortOn}
          Features={Features}
          setGridPageSize={setGridPageSize}
          refreshInitialization={refreshInitialization}
          assetsPermissions={assetsPermissions}
          accountsPermissions={accountsPermissions}
          {...viewSubGroupsProps[ViewSubGroupsPosition.GRID_PAGINATION_CONTROLS]} />}
      reducer={NAME}
      autoSelectNext={autoSelectNext}
      preference={PREFERENCE}
      title={PageTitle.adminAssets}
      searchLabel={translate('admin-assets:searchAssets')}
      onShowColumnChooser={gridRef.current?.toggleColumnChooser}
      exportContext={ExportContext.asset}
      getFilterQueries={setFilterChips}
      onImport={onImport}
      onCarbImport={onCarbImport}
      hasCarbAssetImport={hasCarbAssetImport}
      onExport={onExport}
      onAdd={onToggleAddForm}
      hasAddPermission={assetsPermissions?.hasAddPermission}
      onUpdateGridColumns={onUpdateGridColumns}
      onRefresh={onRefresh}
      isItemSelected={isAssetSelected}
      includeGlobalSearch={includeGlobalSearch}
      isCtcAdmin={isCtcAdmin}
      getSearchParams={getSearchParams}
      searchInstance={mappings}
      totalRowsCount={totalRowsCount}
      selectedItemsCount={selectedAssets?.length}
      hasExportLimit={hasExportLimit}
      showBulkDialog={showBulkDialog}
      disableExport={disableExport}
      search={filtersSearchCriteria}
      sortOn={sortOn}
      getExportColumnDefinitions={gridRef.current?.getExportColumnDefinitions}
      showNotificationDialog={showNotificationDialog}
      {...viewSubGroupsProps[ViewSubGroupsPosition.GRID_CTAS]}
      includeExport />
      {showAddAsset && (
        <AddAssetEditor isCtcAdmin={isCtcAdmin}
          assetsPermissions={assetsPermissions}
          isReadOnly={isReadOnly}
          includeAssetAssociation={includeAssetAssociation}
          showAddTypeDefault={showAddTypeDefault}
          showAddInstallDateDefault={showAddInstallDateDefault}
          globalGroup={globalGroup}
          includeGroupProperties={includeGroupProperties}
          includeDeviceGridCheckboxes={includeDeviceGridCheckboxes}
          includeAssetGridCheckboxes={includeAssetGridCheckboxes}
          isInfiniteScroll={isInfiniteScroll}
          showTreeRefresh={showTreeRefresh} />
      )}
      {showImportAsset && (
        <ImportAssetsContainer overrideAssetTypeColumn
          onDownload={onExport}
          account={globalGroup} />
      )}
      <Render.If condition={showCarbImport}>
        <ImportCarbAssetsContainer setShowCarbImport={setShowCarbImport}
          account={globalGroup}
          onComplete={onComplete} />
      </Render.If>
      {!isCtcAdmin && (showEditAsset || showViewAsset) && (
        <EditAssetEditor isCtcAdmin={isCtcAdmin}
          isReadOnly={isReadOnly}
          isViewDialog={showViewAsset}
          includeAssetAssociation={includeAssetAssociation}
          includeFilteredAssetAssociation={includeFilteredAssetAssociation}
          globalGroup={globalGroup}
          onEdit={onEdit}
          onAudit={onAudit}
          assetsPermissions={assetsPermissions}
          includeGroupProperties={includeGroupProperties}
          includeDeviceGridCheckboxes={includeDeviceGridCheckboxes}
          includeAssetGridCheckboxes={includeAssetGridCheckboxes}
          isInfiniteScroll={isInfiniteScroll}
          showTreeRefresh={showTreeRefresh} />
      )}
      {(showAuditAsset || showAuditGroup) && (
        <AuditDialog enableDateFilter={enableDateFilter}
          rowMenusEnabled={isCtcAdmin}
          showDifferences={isCtcAdmin}
          showAuditVersion={isCtcAdmin}
          item={auditItem}
          entity={auditEntity}
          onCloseDialog={onAuditClose}
          onViewAuditChanges={onViewAuditChanges} />
      )}
      {showEditGroup && <EditGroupEditor isCtcAdmin={isCtcAdmin}
        includeDeviceGridCheckboxes={includeDeviceGridCheckboxes}
        includeAssetGridCheckboxes={includeAssetGridCheckboxes} />}
      {(shouldShowAuditChangesDialog || showAuditGroupChanges) && auditSelectedRecord && (
        <ViewAuditChangesDialog auditRecord={auditSelectedRecord}
          onCloseDialog={onViewAuditChangesClose}
          entity={auditEntity} />
      )}
      {showBulkEditAsset && <BulkEditAssetEditor isCtcAdmin={isCtcAdmin} globalGroup={globalGroup}/>}
      {showMoveAsset && <BulkMoveAssetEditor isCtcAdmin={isCtcAdmin}
        globalGroup={globalGroup}
        setItemSelected={setIsAssetSelected}/>}
      {viewMovedAssets && (
        <MovedEntitiesEditor entity={translate('common:entityLabels.asset')}
          isExporting={isExporting}
          movedEntities={movedAssets}
          errors={movedAssetsErrors}
          onCancel={onCancelViewMovedAssetsDialog}
          onMovedDevicesExportClick={onMovedAssetsExportClick} />
      )}
      {restrictDeletePrompt && (
        <AssetDeleteRestrictPrompt show={restrictDeletePrompt}
          message={translate('admin-assets:import.errors.restrictDelete', {asset: deleteAssetName})}
          titleMessage={translate('admin-assets:import.warning.restrictDeleteTitle')}
          onDeleteRestrictCancel={onDeleteRestrictCancel}/>
      )}
      {restrictBulkDeletePrompt && (
        <AssetDeleteRestrictPrompt show={restrictBulkDeletePrompt}
          message={translate('admin-assets:import.errors.restrictDeleteContinue', {asset: deleteAssetName})}
          titleMessage={translate('admin-assets:import.warning.restrictDeleteTitle')}
          onDeleteRestrictCancel={onDeleteRestrictCancel}/>
      )}
      <AssetDeletePrompt show={isEditing}
        onDeleteConfirm={onDeleteConfirm}
        onDeleteCancel={onDeleteCancel}
        overrideMessage={getDeleteMessage}
        entity={translate('common:entityLabels.asset')}
        asset={selected}
        result={result}
        loading={loading}
        error={error} />
      {(bulkDeleteIsEditing || bulkDeleteRecord) &&
        (<BulkDeletePrompt show={bulkDeleteIsEditing}
          items={bulkDeleteSelected}
          entity={translate('common:entityLabels.assets')}
          result={bulkDeleteResult}
          loading={bulkDeleteLoading}
          error={bulkDeleteError}
          overrideMessage={getDeleteMessage}
          deletedItems={bulkDeleteRecord}
          onDeleteConfirm={onBulkDeleteConfirm}
          onDeleteCancel={onBulkDeleteCancel} />)}
      {showBulkEditGroup && isCtcAdmin && (
        <BulkEditGroupEditor areTotalRowsSelected={areTotalRowsSelected}
          search={filtersSearchCriteria}
          filters={filterChips}
          onCloseDialog={onBulkEditGroupClose}
          selected={bulkEditGroupSelectedAssets}
          account={globalGroup}
          entityLabel={translate('common:entityLabels.assets')}
          entity={Entity.asset}
          onRefresh={onRefresh} />
      )}
      {showGroup && <ViewGroupEditor includeDeviceGridCheckboxes={includeDeviceGridCheckboxes}
        includeAssetGridCheckboxes={includeAssetGridCheckboxes} />}
      {!isCtcAdmin && showViewDevice && <ViewDeviceEditor />}
      {showSuccess && (
        <IonSnackbar show
          message={translate('common:editors.shared.successMoved', {
            entity : translate('common:entityLabels.assets')
          })}
          partialSuccessState={partialStatusMessageFormatter(movedAssets, selectedAssetsToMove)}
          customDismissLabel={translate('common:dismiss')}
          actionLabel={translate('common:viewRecords')}
          onAction={onViewRecords}
          onDismiss={onCloseSnack} />
      )}
      <SnackbarMessage show={showBulkSnackbar}
        message={bulkSnackbarMessage}
        duration={8000}
        onDismiss={dismissBulkSnackbar} />
    </>
  );
};
export default AdminAssets;
